import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { 
  Play, 
  Pause, 
  Download, 
  Music, 
  Volume2, 
  Moon,
  ChevronRight,
  Loader2
} from "lucide-react";

const RamadanAudioSection = () => {
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPlaying, setCurrentPlaying] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  useEffect(() => {
    fetchAudioFiles();
  }, []);

  const fetchAudioFiles = async () => {
    try {
      const response = await axios.get(`${API}/media?media_type=audio&committee=mosque-dawah&limit=6`);
      setAudioFiles(response.data);
    } catch (error) {
      console.error("Error fetching audio:", error);
    } finally {
      setLoading(false);
    }
  };

  const handlePlay = (audio) => {
    if (currentPlaying?.media_id === audio.media_id) {
      // Toggle play/pause for same audio
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
      }
    } else {
      // Play new audio
      setCurrentPlaying(audio);
      setIsPlaying(true);
    }
  };

  const handleDownload = async (audio) => {
    try {
      const response = await axios.get(`${API}/media/${audio.media_id}/download`);
      if (response.data.file_data) {
        const link = document.createElement('a');
        link.href = `data:${response.data.mime_type};base64,${response.data.file_data}`;
        link.download = response.data.file_name;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }
    } catch (error) {
      console.error("Download error:", error);
    }
  };

  const handleAudioEnded = () => {
    setIsPlaying(false);
  };

  const formatDuration = (seconds) => {
    if (!seconds) return "";
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <section className="section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900">
        <div className="container-custom text-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-white mx-auto" />
        </div>
      </section>
    );
  }

  if (audioFiles.length === 0) {
    return null; // Don't show section if no audio files
  }

  return (
    <section className="section-padding bg-gradient-to-br from-primary-900 via-primary-800 to-emerald-900 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-10 left-10 w-32 h-32 border border-white rounded-full"></div>
        <div className="absolute bottom-10 right-10 w-48 h-48 border border-white rounded-full"></div>
        <div className="absolute top-1/2 left-1/4 w-24 h-24 border border-white rounded-full"></div>
      </div>

      <div className="container-custom relative z-10">
        {/* Header */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm mb-4">
            <Moon className="w-5 h-5 text-yellow-300" />
            <span className="text-white font-medium">Ramadan Special</span>
          </div>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-white mb-4">
            Islamic Audio Collection
          </h2>
          <p className="text-primary-200 max-w-2xl mx-auto">
            Listen to Quran recitations, lectures, and Islamic reminders. Perfect for the blessed month of Ramadan.
          </p>
        </div>

        {/* Audio Player - Current Playing */}
        {currentPlaying && (
          <Card className="mb-8 p-6 bg-white/10 backdrop-blur-md border-white/20">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 flex items-center justify-center flex-shrink-0">
                <Volume2 className="w-8 h-8 text-white" />
              </div>
              <div className="flex-grow min-w-0">
                <p className="text-white font-semibold truncate">{currentPlaying.title}</p>
                <p className="text-primary-200 text-sm">{currentPlaying.description || "Mosque and Da'wah"}</p>
                <audio 
                  ref={audioRef}
                  src={`${API.replace('/api', '')}${currentPlaying.file_url}`}
                  onEnded={handleAudioEnded}
                  autoPlay
                  className="w-full mt-2"
                  controls
                />
              </div>
            </div>
          </Card>
        )}

        {/* Audio Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {audioFiles.map((audio) => (
            <Card 
              key={audio.media_id} 
              className={`p-4 transition-all duration-300 ${
                currentPlaying?.media_id === audio.media_id 
                  ? 'bg-white/20 border-yellow-400/50' 
                  : 'bg-white/10 border-white/20 hover:bg-white/15'
              } backdrop-blur-sm`}
              data-testid={`audio-card-${audio.media_id}`}
            >
              <div className="flex items-center gap-3">
                <button
                  onClick={() => handlePlay(audio)}
                  className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 transition-all ${
                    currentPlaying?.media_id === audio.media_id && isPlaying
                      ? 'bg-yellow-400 text-primary-900'
                      : 'bg-white/20 text-white hover:bg-white/30'
                  }`}
                  data-testid={`play-btn-${audio.media_id}`}
                >
                  {currentPlaying?.media_id === audio.media_id && isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </button>
                <div className="flex-grow min-w-0">
                  <p className="text-white font-medium text-sm truncate">{audio.title}</p>
                  <p className="text-primary-300 text-xs truncate">{audio.description || "Islamic Audio"}</p>
                </div>
                <button
                  onClick={() => handleDownload(audio)}
                  className="p-2 rounded-full hover:bg-white/20 text-white/80 hover:text-white transition-colors"
                  title="Download"
                  data-testid={`download-btn-${audio.media_id}`}
                >
                  <Download className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>

        {/* View More Link */}
        <div className="text-center mt-8">
          <Button 
            asChild 
            className="bg-white text-primary-900 hover:bg-primary-50 rounded-full px-8"
            data-testid="view-all-audio-btn"
          >
            <Link to="/media-gallery">
              <Music className="w-4 h-4 mr-2" />
              View Full Audio Library
              <ChevronRight className="w-4 h-4 ml-2" />
            </Link>
          </Button>
        </div>
      </div>
    </section>
  );
};

export default RamadanAudioSection;
